

"use client"

import { useState, useEffect } from "react"
import { Link, useParams, useNavigate } from "react-router-dom"
import axios from "axios"
import { baseUrl } from "../config/baseUrl"
import empty from "../assets/images/empty-box.png"
import Swal from "sweetalert2"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faQuestionCircle,
  faPlus,
  faEdit,
  faTrash,
  faCheck,
  faTimes,
  faSearch,
  faArrowLeft,
  faLanguage,
  faSpinner,
} from "@fortawesome/free-solid-svg-icons"
import { toast } from "react-toastify"

export default function TestQuestions() {
  const { id } = useParams()
  const navigate = useNavigate()

  const [testQuestions, setTestQuestions] = useState([])
  const [loading, setLoading] = useState(false)
  const [loadingTest, setLoadingTest] = useState(true)
  const [searchTerm, setSearchTerm] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [language, setLanguage] = useState("english")
  const [testDetails, setTestDetails] = useState(null)
  const [subjectFilter, setSubjectFilter] = useState("")
  const [topicFilter, setTopicFilter] = useState("")

  // API data states
  const [subjects, setSubjects] = useState([])
  const [topics, setTopics] = useState([])
  const [loadingSubjects, setLoadingSubjects] = useState(false)
  const [loadingTopics, setLoadingTopics] = useState(false)

  const [sequenceInputs, setSequenceInputs] = useState({})

  useEffect(() => {
    fetchTestQuestions()
    fetchSubjects()
    fetchTopics()
  }, [id])

  // Fetch subjects from API
  const fetchSubjects = async () => {
    try {
      setLoadingSubjects(true)
      const response = await axios.get(`${baseUrl}/subject/list`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response.data.status && Array.isArray(response.data.data)) {
        setSubjects(response.data.data)
      } else {
        console.error("Error fetching subjects:", response.data)
        setSubjects([])
      }
    } catch (error) {
      console.error("Error fetching subjects:", error)
      setSubjects([])
    } finally {
      setLoadingSubjects(false)
    }
  }

  // Fetch topics from API
  const fetchTopics = async () => {
    try {
      setLoadingTopics(true)
      const response = await axios.get(`${baseUrl}/topic/list`, {
        headers: {
          Authorization: localStorage.getItem("token"),
        },
      })

      if (response.data.status && Array.isArray(response.data.data)) {
        setTopics(response.data.data)
      } else {
        console.error("Error fetching topics:", response.data)
        setTopics([])
      }
    } catch (error) {
      console.error("Error fetching topics:", error)
      setTopics([])
    } finally {
      setLoadingTopics(false)
    }
  }

  const fetchTestQuestions = async () => {
    try {
      setLoading(true)
      setLoadingTest(true)

      const response = await axios.post(
        `${baseUrl}/question_selected/test-questions`,
        { testId: id },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      )

      if (response.data.status) {
        const questionsData = response.data.data || []
        setTestQuestions(questionsData)
        setSequenceInputs({})
      } else {
        Swal.fire({
          title: "Error!",
          text: response.data.message || "Failed to fetch test questions",
          icon: "error",
        })
      }
    } catch (error) {
      console.error("Error fetching test questions:", error)
    } finally {
      setLoading(false)
      setLoadingTest(false)
    }
  }

  const handleStatus = async (questionId) => {
    try {
      setTestQuestions((prevQuestions) =>
        prevQuestions.map((q) =>
          q._id === questionId ? { ...q, status: q.status === "active" ? "inactive" : "active" } : q,
        ),
      )

      Swal.fire({
        title: "Status Changed!",
        text: "Question status updated successfully",
        icon: "success",
        timer: 1500,
        showConfirmButton: false,
      })
    } catch (error) {
      Swal.fire({
        title: "Error!",
        text: "Failed to update status",
        icon: "error",
      })
    }
  }

  const { id: testId } = useParams()

  const handleRemoveQuestion = async (questionId) => {
    const result = await Swal.fire({
      title: "Remove Question?",
      text: "Are you sure you want to remove this question from the test?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, remove it!",
    })

    if (result.isConfirmed) {
      try {
        const response = await fetch(`${baseUrl}/question_selected/selected-question-delete`, {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: localStorage.getItem("token"),
          },
          body: JSON.stringify({
            questionId,
            testId,
          }),
        })

        if (!response.ok) {
          throw new Error("Failed to remove question from test")
        }

        await response.json()

        setTestQuestions((prevQuestions) => prevQuestions.filter((q) => q._id !== questionId))

        Swal.fire({
          title: "Removed!",
          text: "Question has been removed from the test.",
          icon: "success",
          timer: 1500,
          showConfirmButton: false,
        })
      } catch (error) {
        console.error("Error removing question:", error)
        Swal.fire({
          title: "Error!",
          text: "Failed to remove question from test",
          icon: "error",
        })
      }
    }
  }

  // Helper function to get question text based on language
  const getQuestionText = (question, lang) => {
    switch (lang.toLowerCase()) {
      case "english":
        return question?.question_english || ""
      case "hindi":
        return question?.question_hindi || ""
      case "bengali":
        return question?.question_bengali || ""
      default:
        return question?.question_english || ""
    }
  }

  // Helper function to get option text based on language
  const getOptionText = (question, lang, optionKey) => {
    switch (lang.toLowerCase()) {
      case "english":
        return question?.[`option_${optionKey.toLowerCase()}_english`] || ""
      case "hindi":
        return question?.[`option_${optionKey.toUpperCase()}_hindi`] || ""
      case "bengali":
        const optionMap = { a: "1", b: "2", c: "3", d: "4", e: "5" }
        return question?.[`option_${optionMap[optionKey.toLowerCase()]}_bengali`] || ""
      default:
        return question?.[`option_${optionKey.toLowerCase()}_english`] || ""
    }
  }

  // Helper function to get explanation
  const getExplanation = (question, lang) => {
    switch (lang.toLowerCase()) {
      case "english":
        return question?.explanation_english || ""
      case "hindi":
        return question?.explanation_hindi || ""
      case "bengali":
        return question?.explanation_bengali || ""
      default:
        return question?.explanation_english || ""
    }
  }

  // Helper functions to get subject and topic names by ID
  const getSubjectNameById = (subjectId) => {
    if (!subjectId) return "N/A"

    // If it's already a name (string), return it
    if (typeof subjectId === "string" && subjectId.length !== 24) {
      return subjectId
    }

    // Find subject by ID
    const subject = subjects.find((s) => s._id === subjectId)
    return subject ? subject.name : "Unknown Subject"
  }

  const getTopicNameById = (topicId) => {
    if (!topicId) return "N/A"

    // If it's already a name (string), return it
    if (typeof topicId === "string" && topicId.length !== 24) {
      return topicId
    }

    // Find topic by ID
    const topic = topics.find((t) => t._id === topicId)
    return topic ? topic.name : "Unknown Topic"
  }

  // Get filtered topics based on selected subject
  const getFilteredTopics = () => {
    if (!subjectFilter) return topics

    return topics.filter((topic) => {
      // Handle both populated and non-populated subject field
      const topicSubjectId = typeof topic.subject === "object" ? topic.subject._id : topic.subject
      return topicSubjectId === subjectFilter
    })
  }

  // Filter questions based on search term, status, subject, topic, and handle multilingual search
  const filteredQuestions = testQuestions.filter((question) => {
    const questionText = getQuestionText(question, language)
    const subjectName = getSubjectNameById(question?.subject)
    const topicName = getTopicNameById(question?.topic)

    const matchesSearch =
      questionText?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      subjectName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      topicName?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesStatus = statusFilter === "" || question.status === statusFilter

    const matchesSubject = subjectFilter === "" || question.subject === subjectFilter

    const matchesTopic = topicFilter === "" || question.topic === topicFilter

    return matchesSearch && matchesStatus && matchesSubject && matchesTopic
  })

  const handleQuestionSequenceChange = async (questionId, value) => {
    const newSequence = Number.parseInt(value, 10)
    if (isNaN(newSequence) || newSequence < 1) return

    const currentIndex = testQuestions.findIndex((q) => q._id === questionId)
    if (currentIndex === -1 || currentIndex + 1 === newSequence) return

    try {
      await axios.post(
        `${baseUrl}/question_selected/update-sequence`,
        {
          questionId,
          testId: id,
          targetSequence: newSequence,
        },
        {
          headers: {
            Authorization: localStorage.getItem("token"),
            "Content-Type": "application/json",
          },
        },
      )

      toast.success("Question sequence updated successfully.")
      fetchTestQuestions()
    } catch (error) {
      console.error("Failed to update sequence:", error)
      Swal.fire({
        title: "Error!",
        text: "Failed to update question order.",
        icon: "error",
      })
      fetchTestQuestions()
    }
  }

  const truncateText = (text, maxLength = 80) => {
    if (!text) return ""
    return text.length > maxLength ? text.substring(0, maxLength) + "..." : text
  }

  const formatDate = (dateString) => {
    const date = new Date(dateString)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })
  }

  // Reset topic filter when subject filter changes
  const handleSubjectFilterChange = (e) => {
    setSubjectFilter(e.target.value)
    setTopicFilter("") // Reset topic filter when subject changes
  }

  if (loadingTest) {
    return (
      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-12 text-center py-5">
              <div className="loading-spinner">
                <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-primary" />
              </div>
              <p className="mt-3">Loading test questions...</p>
            </div>
          </div>
        </div>
      </section>
    )
  }

  return (
    <>
      <section className="main-sec">
        <div className="container">
          <div className="row">
            <div className="col-lg-6">
              <div className="dashboard-title">
                <h4 className="dash-head">
                  <FontAwesomeIcon icon={faQuestionCircle} className="me-2" />
                  Test Questions
                </h4>
              </div>
              <div className="custom-bredcump">
                <nav aria-label="breadcrumb">
                  <ol className="breadcrumb">
                    <li className="breadcrumb-item">
                      <Link to="/">Dashboard</Link>
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="/test-series">Tests</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Test Questions
                    </li>
                  </ol>
                </nav>
              </div>
            </div>
            <div className="col-lg-6 d-flex justify-content-end align-items-start">
              <Link to={`/test-series/questions/assign/${id}`} className="btn-add-question me-2">
                <FontAwesomeIcon icon={faPlus} className="me-2" />
                Assign Questions
              </Link>
              <Link to="/test-series" className="btn-add-question" style={{ background: "#4facfe" }}>
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Back to Tests
              </Link>
            </div>
          </div>

          <div className="row">
            <div className="col-lg-12">
              <div className="cards bus-list">
                <div className="bus-filter w-100">
                  <div className="row align-items-baseline">
                    <div className="col-lg-6">
                      <h5 className="card-title">Questions List </h5>
                      <span className="text-danger fw-bold">
                        (To change the sequence, enter the numbers in your preferred order and click the check mark.)
                      </span>
                    </div>
                    <div className="col-lg-6 d-flex justify-content-end">
                      <h5 className="card-title">Assigned Questions ({filteredQuestions.length})</h5>
                    </div>
                  </div>
                </div>

                <div className="search-filter-container">
                  <div className="search-container">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" />
                    <input
                      type="text"
                      className="search-input"
                      placeholder="Search questions, subjects, or topics..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>

                  <select
                    className="filter-select"
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                  >
                    <option value="">All Status</option>
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>

                  <select
                    className="filter-select"
                    value={subjectFilter}
                    onChange={handleSubjectFilterChange}
                    disabled={loadingSubjects}
                  >
                    <option value="">{loadingSubjects ? "Loading subjects..." : "All Subjects"}</option>
                    {subjects.map((subject) => (
                      <option key={subject._id} value={subject._id}>
                        {subject.name}
                      </option>
                    ))}
                  </select>

                  <select
                    className="filter-select"
                    value={topicFilter}
                    onChange={(e) => setTopicFilter(e.target.value)}
                    disabled={loadingTopics || !subjectFilter}
                  >
                    <option value="">
                      {loadingTopics ? "Loading topics..." : !subjectFilter ? "Select subject first" : "All Topics"}
                    </option>
                    {getFilteredTopics().map((topic) => (
                      <option key={topic._id} value={topic._id}>
                        {topic.name}
                      </option>
                    ))}
                  </select>

                  <select className="filter-select" value={language} onChange={(e) => setLanguage(e.target.value)}>
                    <option value="english">
                      <FontAwesomeIcon icon={faLanguage} className="me-1" />
                      English
                    </option>
                    <option value="hindi">हिंदी (Hindi)</option>
                    <option value="bengali">বাংলা (Bengali)</option>
                  </select>
                </div>

                <div className="table-responsive custom-table">
                  <table className="table table-borderless">
                    <thead>
                      <tr>
                        <th>SN</th>
                        <th>Sequence</th>
                        <th>Question</th>
                        <th>Subject & Topic</th>
                        <th>Correct Answer</th>
                        <th>Created</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="loading-spinner">
                            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
                            <p className="mt-2">Loading questions...</p>
                          </td>
                        </tr>
                      ) : filteredQuestions.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="empty-state">
                            <img
                              src={empty || "/placeholder.svg?height=200&width=200"}
                              alt="No questions"
                              width="200px"
                            />
                            <h4>No Questions found!</h4>
                            <p className="text-muted">
                              {searchTerm || statusFilter || subjectFilter || topicFilter
                                ? "Try adjusting your search or filter criteria"
                                : "This test has no questions assigned yet"}
                            </p>
                            <Link to={`/test-series/questions/assign/${id}`} className="btn-add-question mt-3">
                              <FontAwesomeIcon icon={faPlus} className="me-2" />
                              Assign Questions
                            </Link>
                          </td>
                        </tr>
                      ) : (
                        filteredQuestions.map((question, i) => (
                          <tr key={question?._id}>
                            <td>{i + 1}</td>
                            <td className="sequence-control-cell">
                              <div className="d-flex align-items-center gap-1">
                                <input
                                  type="number"
                                  value={sequenceInputs[question._id] ?? question.sequence ?? i + 1}
                                  onChange={(e) =>
                                    setSequenceInputs((prev) => ({
                                      ...prev,
                                      [question._id]: e.target.value,
                                    }))
                                  }
                                  className="sequence-input"
                                  min={1}
                                  max={filteredQuestions.length}
                                  disabled={loading}
                                />
                                <button
                                  className="action-btn view-btn"
                                  onClick={() =>
                                    handleQuestionSequenceChange(question._id, sequenceInputs[question._id])
                                  }
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faCheck} />
                                </button>
                              </div>
                            </td>

                            <td>
                              <div className="question-text">
                                <div
                                  dangerouslySetInnerHTML={{
                                    __html: truncateText(getQuestionText(question, language), 100),
                                  }}
                                />
                              </div>
                            </td>
                            <td>
                              <div>
                                <span className="subject-badge">{getSubjectNameById(question?.subject)}</span>
                                {question?.topic && (
                                  <div className="topic-badge">
                                    {truncateText(getTopicNameById(question.topic), 30)}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="correct-answer">Option {question?.correct_answer || "N/A"}</span>
                            </td>
                            <td>{formatDate(question?.createdAt)}</td>
                            <td>
                              <button
                                className={`status-badge ${question.status === "active" ? "status-active" : "status-inactive"
                                  }`}
                                onClick={() => handleStatus(question._id)}
                                disabled={loading}
                              >
                                {question?.status === "active" ? (
                                  <>
                                    <FontAwesomeIcon icon={faCheck} className="me-1" />
                                    Active
                                  </>
                                ) : (
                                  <>
                                    <FontAwesomeIcon icon={faTimes} className="me-1" />
                                    Inactive
                                  </>
                                )}
                              </button>
                            </td>
                            <td>
                              <div className="action-buttons">
                                <Link to={`/questions/edit/${question?._id}`} className="action-btn edit-btn">
                                  <FontAwesomeIcon icon={faEdit} />
                                  <span className="tooltip-text">Edit</span>
                                </Link>
                                <button
                                  className="action-btn delete-btn"
                                  onClick={() => handleRemoveQuestion(question?._id)}
                                  disabled={loading}
                                >
                                  <FontAwesomeIcon icon={faTrash} />
                                  <span className="tooltip-text">Remove</span>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <style >{`
        .main-sec {
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
          min-height: 100vh;
          padding: 2rem 0;
        }

        .dashboard-title {
          margin-bottom: 1.5rem;
        }

        .dash-head {
          color: #333;
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0;
        }

        .cards {
          background: white;
          border-radius: 15px;
          padding: 2rem;
          box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
          margin-bottom: 2rem;
        }

        .bus-filter {
          margin-bottom: 2rem;
          padding-bottom: 1.5rem;
          border-bottom: 1px solid #eee;
        }

        .card-title {
          color: #333;
          font-weight: 700;
          margin: 0;
        }

        .btn-add-question {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          border: none;
          border-radius: 8px;
          padding: 0.75rem 1.5rem;
          color: white;
          font-weight: 600;
          text-decoration: none;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .btn-add-question:hover {
          transform: translateY(-2px);
          box-shadow: 0 10px 20px rgba(102, 126, 234, 0.3);
          color: white;
          text-decoration: none;
        }

        .search-filter-container {
          display: flex;
          gap: 1rem;
          margin-bottom: 1.5rem;
          flex-wrap: wrap;
          align-items: center;
        }

        .search-container {
          position: relative;
          flex: 1;
          min-width: 250px;
        }

        .search-icon {
          position: absolute;
          left: 0.75rem;
          top: 50%;
          transform: translateY(-50%);
          color: #999;
          z-index: 1;
        }

        .search-input {
          width: 100%;
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem 0.75rem 2.5rem;
          font-size: 0.9rem;
          transition: all 0.3s ease;
        }

        .search-input:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .filter-select {
          border-radius: 8px;
          border: 1px solid #ddd;
          padding: 0.75rem 1rem;
          min-width: 150px;
          font-size: 0.9rem;
          background: white;
          transition: all 0.3s ease;
        }

        .filter-select:focus {
          outline: none;
          border-color: #667eea;
          box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
        }

        .filter-select:disabled {
          background-color: #f8f9fa;
          color: #6c757d;
          cursor: not-allowed;
        }

        .custom-table {
          border-radius: 10px;
          overflow: hidden;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
        }

        .table {
          margin: 0;
          font-size: 0.9rem;
        }

        .table thead th {
          background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
          color: white;
          font-weight: 600;
          border: none;
          padding: 1rem 0.75rem;
          text-align: center;
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .table tbody td {
          padding: 1rem 0.75rem;
          vertical-align: middle;
          border-bottom: 1px solid #f0f0f0;
          text-align: center;
        }

        .table tbody tr:hover {
          background-color: #f8f9ff;
        }

        .question-text {
          text-align: left;
          max-width: 300px;
        }

        .subject-badge {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          color: white;
          padding: 0.25rem 0.75rem;
          border-radius: 15px;
          font-size: 0.75rem;
          font-weight: 600;
          display: inline-block;
          margin-bottom: 0.25rem;
        }

        .topic-badge {
          background: #e9ecef;
          color: #495057;
          padding: 0.25rem 0.5rem;
          border-radius: 10px;
          font-size: 0.7rem;
          display: inline-block;
        }

        .correct-answer {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          display: inline-block;
        }

        .status-badge {
          padding: 0.5rem 1rem;
          border-radius: 20px;
          font-size: 0.8rem;
          font-weight: 600;
          border: none;
          cursor: pointer;
          transition: all 0.3s ease;
          display: inline-flex;
          align-items: center;
        }

        .status-active {
          background: linear-gradient(135deg, #56ab2f 0%, #a8e6cf 100%);
          color: white;
        }

        .status-inactive {
          background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
          color: white;
        }

        .status-badge:hover {
          transform: scale(1.05);
        }

        .status-badge:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .sequence-control-cell {
          min-width: 120px;
        }

        .sequence-input {
          width: 60px;
          text-align: center;
          padding: 0.25rem;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 0.85rem;
        }

        .action-buttons {
          display: flex;
          gap: 0.5rem;
          justify-content: center;
        }

        .action-btn {
          width: 35px;
          height: 35px;
          border-radius: 8px;
          border: none;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: all 0.3s ease;
          position: relative;
          text-decoration: none;
        }

        .view-btn {
          background: linear-gradient(135deg, #4facfe 0%, #00f2fe 100%);
          color: white;
        }

        .edit-btn {
          background: linear-gradient(135deg, #fa709a 0%, #fee140 100%);
          color: white;
        }

        .delete-btn {
          background: linear-gradient(135deg, #ff416c 0%, #ff4b2b 100%);
          color: white;
        }

        .action-btn:hover {
          transform: translateY(-2px);
          box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          color: white;
          text-decoration: none;
        }

        .action-btn:disabled {
          opacity: 0.6;
          cursor: not-allowed;
          transform: none;
        }

        .tooltip-text {
          position: absolute;
          bottom: -35px;
          left: 50%;
          transform: translateX(-50%);
          background: #333;
          color: white;
          padding: 0.25rem 0.5rem;
          border-radius: 4px;
          font-size: 0.75rem;
          opacity: 0;
          pointer-events: none;
          transition: opacity 0.3s ease;
          white-space: nowrap;
          z-index: 1000;
        }

        .action-btn:hover .tooltip-text {
          opacity: 1;
        }

        .empty-state {
          text-align: center;
          padding: 3rem;
        }

        .empty-state img {
          opacity: 0.7;
          margin-bottom: 1rem;
        }

        .empty-state h4 {
          color: #666;
          margin-bottom: 1rem;
        }

        .loading-spinner {
          display: flex;
          flex-direction: column;
          justify-content: center;
          align-items: center;
          padding: 3rem;
          color: #667eea;
        }

        .breadcrumb {
          background: none;
          padding: 0;
          margin: 0;
        }

        .breadcrumb-item a {
          color: #667eea;
          text-decoration: none;
        }

        .breadcrumb-item a:hover {
          color: #764ba2;
        }

        .breadcrumb-item.active {
          color: #6c757d;
        }

        @media (max-width: 768px) {
          .search-filter-container {
            flex-direction: column;
          }

          .search-container,
          .filter-select {
            min-width: 100%;
          }

          .table-responsive {
            font-size: 0.8rem;
          }

          .table thead th,
          .table tbody td {
            padding: 0.5rem 0.25rem;
          }

          .question-text {
            max-width: 200px;
          }

          .action-buttons {
            flex-direction: column;
            gap: 0.25rem;
          }

          .action-btn {
            width: 30px;
            height: 30px;
          }

          .btn-add-question {
            padding: 0.5rem 1rem;
            font-size: 0.85rem;
          }
        }

        @media (max-width: 576px) {
          .main-sec {
            padding: 1rem 0;
          }

          .cards {
            padding: 1rem;
          }

          .dash-head {
            font-size: 1.5rem;
          }

          .table thead th {
            font-size: 0.7rem;
            padding: 0.75rem 0.25rem;
          }

          .table tbody td {
            padding: 0.75rem 0.25rem;
            font-size: 0.75rem;
          }

          .question-text {
            max-width: 150px;
          }
        }
      `}</style>
    </>
  )
}
